import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { ratePlanService } from 'api/services/ratePlanService';
import type { RatePlan, RatePlanDetail, Property, RoomCategory } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface RatePlanViewModalProps {
  isOpen: boolean;
  ratePlan: RatePlan | null;
  propertyLookup: Record<string, Property>;
  roomCategoryLookup: Record<string, RoomCategory>;
  onClose: () => void;
  onEdit?: (ratePlan: RatePlan) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (ratePlanId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// CONSTANTS
// ============================================================================

const RATE_TYPE_LABELS: Record<string, string> = {
  Rack: 'Rack',
  Standard: 'Standard',
  Corporate: 'Corporate',
  Agent: 'Agent',
  OTA: 'OTA',
  Promotional: 'Promotional',
  Weekend: 'Weekend',
  Seasonal: 'Seasonal',
};

const DAY_LABELS: Record<string, string> = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
  Sun: 'Sunday',
};

// ============================================================================
// COMPONENT
// ============================================================================

const RatePlanViewModal: React.FC<RatePlanViewModalProps> = ({
  isOpen,
  ratePlan,
  propertyLookup,
  roomCategoryLookup,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [details, setDetails] = useState<RatePlanDetail[]>([]);

  // Fetch details when modal opens
  useEffect(() => {
    if (!isOpen || !ratePlan) { setDetails([]); return; }
    const fetchDetails = async () => {
      try {
        const response = await ratePlanService.getById(ratePlan.id);
        setDetails(response.data?.details || []);
      } catch { setDetails([]); }
    };
    fetchDetails();
  }, [isOpen, ratePlan]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !ratePlan) return;
    minimize({
      id: `${tabId}-view-modal-${ratePlan.id}`,
      title: `View - ${ratePlan.rateName}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(ratePlan.id),
    });
    onClose();
  }, [tabId, moduleName, ratePlan, minimize, onClose, onRestoreView]);

  if (!isOpen || !ratePlan) return null;

  const property = ratePlan.propertyId ? propertyLookup[ratePlan.propertyId] : null;
  const propertyDisplay = property ? `${property.propertyCode} - ${property.propertyName}` : '-';

  const roomCategory = ratePlan.roomCategoryId ? roomCategoryLookup[ratePlan.roomCategoryId] : null;
  const roomCategoryDisplay = roomCategory ? `${roomCategory.categoryCode} - ${roomCategory.categoryName}` : '-';

  const formatRate = (rate?: number) => rate != null ? rate.toFixed(2) : '-';
  const formatDate = (date?: string) => {
    if (!date) return '-';
    try { return new Date(date).toLocaleDateString(); } catch { return '-'; }
  };
  const formatDays = (dayOfWeek?: string) => {
    if (!dayOfWeek) return '-';
    return dayOfWeek.split(',').map(d => DAY_LABELS[d.trim()] || d.trim()).join(', ');
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Rate Plan Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Basic Information --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Rate Name" value={ratePlan.rateName} />
            <ReadOnlyField label="Rate Type" value={ratePlan.rateType ? (RATE_TYPE_LABELS[ratePlan.rateType] || ratePlan.rateType) : undefined} />
          </div>

          {/* --- Linked Entities --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Property" value={propertyDisplay} />
            <ReadOnlyField label="Room Category" value={roomCategoryDisplay} />
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${ratePlan.isActive ? 'active' : 'inactive'}`}>
                  {ratePlan.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* --- Room Rates --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Room Rates</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Base Rate" value={formatRate(ratePlan.baseRate)} />
              <ReadOnlyField label="Minimum Rate" value={formatRate(ratePlan.minimumRate)} />
              <ReadOnlyField label="Extra Adult Rate" value={formatRate(ratePlan.extraAdultRate)} />
              <ReadOnlyField label="Extra Child Rate" value={formatRate(ratePlan.extraChildRate)} />
            </div>
          </div>

          {/* --- Meal Plan Rates (Details) --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
              Meal Plan Rates
              {details.length > 0 && <span style={{ fontWeight: 400, fontSize: '0.75rem', marginLeft: '0.5rem', color: 'var(--text-tertiary)' }}>({details.length})</span>}
            </h3>
            {details.length === 0 ? (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', padding: '0.5rem 0' }}>No meal plan details.</div>
            ) : (
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.375rem', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Meal Plan</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Meal Adult Rate</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Meal Child Rate</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((d, idx) => (
                      <tr key={d.id} style={{ borderBottom: idx < details.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                        <td style={{ padding: '0.375rem 0.75rem', fontWeight: 500 }}>
                          <span style={{ fontWeight: 600 }}>{d.mealPlanCode}</span>
                          <span style={{ color: 'var(--text-secondary)', marginLeft: '0.375rem' }}>- {d.mealPlanName}</span>
                        </td>
                        <td style={{ padding: '0.375rem 0.75rem', textAlign: 'right' }}>{formatRate(d.mealAdultRate)}</td>
                        <td style={{ padding: '0.375rem 0.75rem', textAlign: 'right' }}>{formatRate(d.mealChildRate)}</td>
                        <td style={{ padding: '0.375rem 0.75rem', textAlign: 'center' }}>
                          {d.isDefaultMealPlan && <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Default</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* --- Age Limits --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Age Configuration</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Child Age Limit" value={ratePlan.childAgeLimit} />
              <ReadOnlyField label="Chargeable Child Age" value={ratePlan.chargeableChildAge} />
              <ReadOnlyField label="Infant Age Limit" value={ratePlan.infantAgeLimit} />
            </div>
          </div>

          {/* --- Validity & Restrictions --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Validity & Restrictions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Valid From" value={formatDate(ratePlan.validFrom)} />
              <ReadOnlyField label="Valid To" value={formatDate(ratePlan.validTo)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Applicable Days" value={formatDays(ratePlan.dayOfWeek)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Min Nights" value={ratePlan.minNights} />
              <ReadOnlyField label="Max Nights" value={ratePlan.maxNights} />
              <ReadOnlyField label="Advance Booking Days" value={ratePlan.advanceBookingDays} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Close to Arrival" value={ratePlan.closeToArrival} />
              <ReadOnlyField label="Close to Departure" value={ratePlan.closeToDeparture} />
              <ReadOnlyField label="Tax Inclusive" value={ratePlan.includeTaxes} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(ratePlan);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default RatePlanViewModal;
